import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";

import {
  StzhPiMenuClosedEvent,
  StzhPiMenuCloseEvent,
  StzhPiMenuItemClickEvent,
  StzhPiMenuOpenedEvent,
  StzhPiMenuOpenEvent,
  StzhPiPagetitleMenuItem,
} from "../../../index";

import { waitForEvent } from "../../../utils/event-utils";
import { animateTo, shimKeyframesAutoValues, stopAnimations } from "../../../utils/animation-utils";
import { StzhPiMenuLocalization } from "./stzh-pi-menu.localization";

const ANIMATION_SHOW = {
  keyframes: [
    { height: "0", opacity: "0" },
    { height: "100%", opacity: "1" },
  ],
  options: { duration: 300, easing: "linear" },
};

const ANIMATION_HIDE = {
  keyframes: [
    { height: "100%", opacity: "1" },
    { height: "0", opacity: "0" },
  ],
  options: { duration: 300, easing: "linear" },
};

let accordionItemCounter = 0;

// const CLASS_BODY_OPEN = "stzh-header-open";


@Component({
  tag: "stzh-pi-menu",
  styleUrl: "stzh-pi-menu.scss",
  shadow: true,
})
export class StzhPiMenu {
  /** Translation strings */
  @Prop() localization: StzhPiMenuLocalization;

  /** Menu navigation items */
  @Prop() menuItems: StzhPiPagetitleMenuItem[] | string = [];
  private _menuItems: StzhPiPagetitleMenuItem[];

  /** Whether fold is open */
  @Prop({ mutable: true, reflect: true }) open: boolean = false;

  /** Heading title */
  @Prop() heading: string = "";

  /** Heading title */
  @Prop() subheading: string = "";

  /** Heading level, for accessibility, no visual impact */
  @Prop({ reflect: true }) headingLevel: "1" | "2" | "3" | "4" = "3";

  /** If the menu is fixed (when otherwise no longer in viewport) or statically positioned.  */
  @State() menuIsFixed: boolean = false;

  /** Used to sync width of stzh-pi-menu-positioner (in-flow element) with stzh-pi-menu (gets taken out of flow with menuIsFixed, but must stay the same width) */
  @State() entangledMenuSize: number = 20;

  @State() showBackdrop: boolean = false;


  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the button element.
   * Default value will be taken from `heading` prop.
   */
  @Prop() analyticsId: string;

  @Element() element: HTMLStzhPiMenuElement;

  /** Open event */
  @Event() stzhOpen: EventEmitter<StzhPiMenuOpenEvent>;

  /** Opened event (after animation) */
  @Event() stzhOpened: EventEmitter<StzhPiMenuOpenedEvent>;

  /** Close event */
  @Event() stzhClose: EventEmitter<StzhPiMenuCloseEvent>;

  /** Closed event (after animation) */
  @Event() stzhClosed: EventEmitter<StzhPiMenuClosedEvent>;

  /** Metanav item click event */
  @Event() stzhMenuItemClick: EventEmitter<StzhPiMenuItemClickEvent>;

  @Watch("menuItems")
  menuItemsWatcher(newValue: StzhPiPagetitleMenuItem[] | string) {
    if (typeof newValue === "string") {
      this._menuItems = JSON.parse(newValue);
    } else {
      this._menuItems = newValue;
    }

    this._menuItems = this._menuItems.map((menuItem, index) => ({
      id: `submenu-${index}`,
      ...menuItem,
    }));
  }

  @Watch("open")
  async openWatcher(newValue: boolean) {
    if (!this.bodyElement) {
      return;
    }

    if (newValue) {
      if (this.toggledByMethod) {
        this.stzhOpen.emit({
          component: "stzh-pi-menu",
        });
      }

      await stopAnimations(this.bodyElement);
      this.bodyElement.hidden = false;

      await animateTo(
        this.bodyElement,
        shimKeyframesAutoValues(ANIMATION_SHOW.keyframes, {
          height: this.bodyElement.scrollHeight,
        }),
        ANIMATION_SHOW.options
      );

      // this.bodyElement.style.height = `calc(100% - ${this.headingElement.clientHeight}px)`;

      if (this.toggledByMethod) {
        this.stzhOpened.emit({
          component: "stzh-pi-menu",
        });
      }
    } else {
      if (this.toggledByMethod) {
        this.stzhClose.emit({
          component: "stzh-pi-menu",
        });
      }

      await stopAnimations(this.bodyElement);

      await animateTo(
        this.bodyElement,
        shimKeyframesAutoValues(ANIMATION_HIDE.keyframes, {
          height: this.bodyElement.scrollHeight,
        }),
        ANIMATION_HIDE.options
      );

      this.bodyElement.hidden = true;
      this.bodyElement.style.height = "0px";

      if (this.toggledByMethod) {
        this.stzhClosed.emit({
          component: "stzh-pi-menu",
        });
      }
    }

    this.toggledByMethod = false;
  }

  /** Show piMenu item content */
  @Method()
  async show() {
    if (this.open) {
      return;
    }

    // this.menuIsFixed && document.body.classList.add(CLASS_BODY_OPEN);
    this.showBackdrop = true;
    this.toggledByMethod = true;
    this.open = true;
    return waitForEvent(this.element, "stzhOpened");
  }

  /** Hide content */
  @Method()
  async hide() {
    if (!this.open) {
      return;
    }

    // document.body.classList.remove(CLASS_BODY_OPEN);
    this.showBackdrop = false;
    this.toggledByMethod = true;
    this.open = false;
    return waitForEvent(this.element, "stzhClosed");
  }

  /** Toggle piMenu item content */
  @Method()
  async toggle() {
    if (this.open) {
      return await this.hide();
    } else {
      return await this.show();
    }
  }

  @State() rootTabindex: number = 0;

  private toggledByMethod: boolean = false;
  private bodyElement: HTMLDivElement;
  private buttonElement: HTMLButtonElement;
  private piMenuId: string;
  private focusedByInput: boolean = false;

  private handleHeaderClick = () => {
    this.toggle();
  };

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.buttonElement.focus();
    }

    this.focusedByInput = false;
  };

  private handleMenuItemClick = (event: MouseEvent) => {
    this.stzhMenuItemClick.emit({
      component: "stzh-pi-menu",
      originalEvent: event,
    });
  };

  private handleBackdropClick = async () => {
    this.showBackdrop = false;
    this.hide();
  };

  private setEntangledMenuSize = () => {
    const menuEl = this.element.shadowRoot.querySelector('.stzh-pi-menu-positioner');
    this.entangledMenuSize = menuEl.getBoundingClientRect().width;
  }

  private menuObserver: IntersectionObserver;
  private resizeTimeout: number;

  private handleMenuIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      if (entry.intersectionRatio < 1 && entry.boundingClientRect.top < 20) {
        this.menuIsFixed = true;
      }
      else {
        this.menuIsFixed = false;
      }
    })
  }

  async componentWillLoad() {
    this.piMenuId = `stzh-pi-menu-${accordionItemCounter++}`;
    this.menuItemsWatcher(this.menuItems);
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "pi-pagetitle");
    }
  }

  componentDidLoad() {
    this.bodyElement.hidden = !this.open;

    this.menuObserver = new IntersectionObserver(this.handleMenuIntersection, { rootMargin: "0px", threshold: 1 });
    const menuEl = this.element.shadowRoot.querySelector('.stzh-pi-menu-positioner');
    this.menuObserver.observe(menuEl);
    this.setEntangledMenuSize();

    window.addEventListener('resize', () => {
      if (this.resizeTimeout) {
        window.cancelAnimationFrame(this.resizeTimeout);
      }
      this.resizeTimeout = window.requestAnimationFrame(() => {
        this.setEntangledMenuSize();
      });
    }, false);
  }

  disconnectedCallback() {
    if (this.menuObserver) {
      this.menuObserver.disconnect();
    }
    window.removeEventListener('resize', () => {
      if (this.resizeTimeout) {
        window.cancelAnimationFrame(this.resizeTimeout);
      }
    });
  }

  render() {
    const Heading = `h${this.headingLevel}`;
    const classes = {
      "stzh-pi-menu": true,
      "stzh-pi-menu--is-open": this.open,
      "stzh-pi-menu--fixed": this.menuIsFixed,
    };

    const backdropClasses = {
      "stzh-pi-menu__backdrop": true,
      "stzh-pi-menu__backdrop--visible": this.showBackdrop,
    }

    return (
      <Host tabindex={this.rootTabindex} onFocus={this.onRootFocus}>
        <div class="stzh-pi-menu-positioner">
          <div class={classes} style={{ width: `${(this.open ? Math.max(300, this.entangledMenuSize) : this.entangledMenuSize).toString()}px` }}>
            <div class={backdropClasses} onClick={this.handleBackdropClick}></div>
            <Heading
              class="stzh-pi-menu__heading"
              onClick={this.handleHeaderClick}
            >
              <button
                ref={el => (this.buttonElement = el as HTMLButtonElement)}
                class="stzh-pi-menu__button"
                aria-expanded={`${this.open ? "true" : "false"}`}
                type="button"
                s-object-id={this.analyticsId || this.heading}
              >
                <span class="stzh-pi-menu__button-text">
                  <span class="stzh-pi-menu__heading-text">{this.heading}</span>
                </span>
                <stzh-icon name="angle-down" class="stzh-pi-menu__button-icon"></stzh-icon>
              </button>
            </Heading>
            <div
              id={`${this.piMenuId}-content`}
              ref={el => (this.bodyElement = el as HTMLDivElement)}
              class="stzh-pi-menu__content"
              aria-labelledby={`${this.piMenuId}-header`}
              role="region"
            >
              <div class="stzh-pi-menu__content-inner">
                <nav class="stzh-pi-menu__nav" aria-label={this.localization.menuLabel}>
                  {this.subheading && <p class="stzh-pi-menu__subheading">{this.subheading}</p>}
                  <ul class="stzh-pi-menu__nav-list is-level-1">
                    {this._menuItems.map(item => (
                      <li class="stzh-pi-menu__nav-list-item">
                        <a
                          href={item.href}
                          onClick={(e: MouseEvent) => this.handleMenuItemClick(e)}
                          class={{
                            "stzh-pi-menu__nav-item": true,
                            "is-level-1": true,
                            active: item.active,
                          }}
                        >
                          {item.label}
                        </a>
                        {item.items?.length > 0 && (
                          <ul id={item.id} class="stzh-pi-menu__nav-list is-level-2">
                            {item.items.map(item => (
                              <li class="stzh-pi-menu__nav-list-item">
                                <a
                                  href={item.href}
                                  onClick={(e: MouseEvent) => this.handleMenuItemClick(e)}
                                  class={{
                                    "stzh-pi-menu__nav-item": true,
                                    "is-level-2": true,
                                    active: item.active,
                                  }}
                                >
                                  {item.label}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>
          </div>
          <button class="stzh-pi-menu__button stzh-pi-menu__button-space-helper" aria-hidden="true" type="button">
            <span class="stzh-pi-menu__button-text">
              <span class="stzh-pi-menu__heading-text">{this.heading}</span>
            </span>
            <stzh-icon name="angle-down" class="stzh-pi-menu__button-icon"></stzh-icon>
          </button>
        </div>
      </Host>
    );
  }
}
